import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Subject, Subscription} from 'rxjs';
import {filter, finalize, switchMap} from 'rxjs/operators';

import {
  DEFAULT_SCROLL_SPEED,
  HISTORY_KEY_SEARCH_TOTAL,
  LIST_TYPE,
  SearchParam
} from '../shared/defines/client.const';
import {GREETING} from '../shared/defines/protocol';
import {FOOTER_ON} from '../shared/components/footer/footer.component';
import {Video} from '../shared/models/video.model';
import {UserService} from '../shared/services/user/user.service';
import {LayoutMgrService} from '../shared/services/layout-mgr/layout-mgr.service';
import {VideoService} from '../shared/services/video/video.service';
import {VideoBookmarkService} from '../shared/services/video/video-bookmark.service';
import {SystemService} from '../shared/services/system/system.service';
import {GlobalEventService} from "../shared/services/global-event/global-event.service";
import {LastIdService} from "../shared/services/last-id/last-id.service";
import {ScrollDownService} from "../shared/services/scroll-down/scroll-down.service";
import {CordovaService} from "../shared/services/cordova/cordova.service";
import {OptionService} from "../shared/services/option/option.service";
import {GreetingService} from "../shared/services/greeting/greeting.service";
import {NormalizeSearchParamKeyword} from '../shared/defines/search-keyword.func';


const NEW_VIDEO_LIMIT = 4;
const VIDEO_PER_LINE = 2;

@Component({
  selector: 'app-main',
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.scss'],
})
export class MainComponent implements OnInit, OnDestroy {
  @ViewChild('container') container: ElementRef;

  readonly FooterOn = FOOTER_ON;
  readonly ListType = LIST_TYPE;
  readonly HISTORY_KEY_SEARCH_TOTAL = HISTORY_KEY_SEARCH_TOTAL;


  public listVideo: Array<Video> = null;
  private LoadVideo$: Subscription = null;

  public get IsLoading(): boolean {
    return this.LoadVideo$ !== null;
  }

  public sendingBookmark = false;

  private greetingClick$ = new Subject<void>();
  greeting: GREETING = null;

  private scrollDown$: Subscription | null = null;

  // 추가로 관리할 subscription 변수 선언
  private deviceReady$?: Subscription;
  private greeting$?: Subscription;
  private login$?: Subscription;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private userService: UserService,
    private videoService: VideoService,
    private videoBmSvc: VideoBookmarkService,
    private systemSvc: SystemService,
    private optionSvc: OptionService,
    // private naviSvc: NavigationService,
    private lastIdSvc: LastIdService,
    private scrollDownSvc: ScrollDownService,
    private globalEventSvc: GlobalEventService,
    public lm: LayoutMgrService,
    private cordovaSvc: CordovaService,
    private greetingSvc: GreetingService,
  ) {
    lm.Reset();

    // cordovaSvc.deviceready 구독 - 로그인 완료 후 서버 정보 확인
    this.deviceReady$ = this.cordovaSvc.deviceready
      .pipe(
        // deviceready 이벤트 후에 로그인 완료될 때까지 대기
        switchMap(() => this.userService.whenLoggedIn())
      )
      .subscribe(() => {
        // 로그인이 완료된 후에 서버 정보 확인
        this.checkServerInfo();
      });

    // 웹 브라우저 환경인 경우 바로 서버 정보 가져오기
    // 주의: 테스트 전용 코드
    // if (this.cordovaSvc.isWeb) {
    //   setTimeout(() => this.checkServerInfo(), 0);
    // }

    // 인사말 처리 (인사말을 반복적으로 눌러도 이전 호출이 자동으로 취소되도록 switchMap 사용)
    // greetingClick$ 구독 (인사말 요청 처리)
    this.greeting$ = this.greetingClick$.pipe(
      switchMap(() => {
        // 여기서 userService.GetGreeting() 호출
        return this.greetingSvc.GetGreeting();
      })
    ).subscribe(
      greeting => {
        // 성공적인 응답을 처리
        console.log('GetGreeting() greeting=', greeting);
        this.greeting = greeting;
      },
      err => {
        // 에러 발생 시 처리
        console.error(err);
        this.greeting = {
          title: '오메, 방문자님!',
          message: '허벌나게 반가워유~좋은 하루 되세유!',
        };
      }
    );

    // userService.loginSuccess$ 구독 (로그인 성공 시 비디오 리스트 로드 및 인사말 재요청)
    this.login$ = this.userService.loginSuccess$.pipe(
      filter(success => success)
    ).subscribe(
      data => {
        console.log(data);
        this.loadVideoList();

        this.greetingClick$.next();
      },
      err => console.error(err)
    );
  }

  ngOnInit(): void {
    // 푸터의 스크롤 버튼 처리.
    this.scrollDown$ = this.scrollDownSvc.pushScrollDown$().subscribe(isFirst => {
      const scrollDelta = DEFAULT_SCROLL_SPEED * (isFirst ? 2 : 1);  // 스크롤 시작할 때 첫번째는 두배로 스크롤 함.

      this.container?.nativeElement.scrollTo({
        top: this.container.nativeElement.scrollTop + scrollDelta,
        behavior: 'auto',
      });
    });
  }

  ngOnDestroy(): void {
    this.lm.CloseYoutubePopup();

    this.scrollDownSvc.popScrollDown$();
    if (this.scrollDown$) {
      this.scrollDown$.unsubscribe();
      this.scrollDown$ = null;
    }
    // LoadVideo$ 구독이 남아 있다면 해제
    if (this.LoadVideo$) {
      this.LoadVideo$.unsubscribe();
      this.LoadVideo$ = null;
    }
    // 추가한 모든 구독 해제
    this.deviceReady$?.unsubscribe();
    this.greeting$?.unsubscribe();
    this.login$?.unsubscribe();
  }

  public goSearch(param: SearchParam): void {
    this.router.navigate(['/total'], {
      queryParams: NormalizeSearchParamKeyword(param),
    }).then();
  }

  private loadVideoList(): void {
    // console.log('MovieSearchComponent::ReloadList() param =', param);

    // this.LoadVideo$ = this.videoService.GetList({limit: NEW_VIDEO_LIMIT})
    this.LoadVideo$ = this.videoService.GetList({limit: NEW_VIDEO_LIMIT})
      .pipe(
        finalize(() => this.LoadVideo$ = null)
      ).subscribe((listVideo: Array<Video>) => {
        console.log('MainComponent::LoadVideoList() listVideo =', listVideo);
        this.listVideo = listVideo;
      });
  }

  public ToggleBookmark(video: Video): void {
    if (this.sendingBookmark) {
      return;
    }
    this.sendingBookmark = true;

    video.bookmark = !video.bookmark;
    this.videoBmSvc.sendBookmark(video.no, video.bookmark).pipe(
      finalize(() => this.sendingBookmark = false)
    ).subscribe(
      data => console.log(data),
      err => console.error(err)
    );
  }


  onClickSaseung($event: MouseEvent) {
    $event.preventDefault();
    $event.stopPropagation();
    this.globalEventSvc.Click($event);

    const queryParams: SearchParam = {
      l: LIST_TYPE.SERVER,
    };
    this.router.navigate(['/saseung'], {queryParams}).then();
  }

  public onClickWord($event: MouseEvent): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.globalEventSvc.Click($event);

    // const [detail_id, list_id] = this.lastIdSvc.getLastWordId();
    const queryParams: SearchParam = {
      // ...(detail_id ? {id: detail_id} : {}),
      // ...(list_id ? {start: list_id} : {}),
      l: LIST_TYPE.SERVER,
    };
    this.router.navigate(['/word'], {queryParams}).then();
  }

  public onClickSajaseungeo($event: MouseEvent): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.globalEventSvc.Click($event);

    // const [detail_id, list_id] = this.lastIdSvc.getLastSajaseungeoId();
    const queryParams: SearchParam = {
      // ...(detail_id ? {id: detail_id} : {}),
      // ...(list_id ? {start: list_id} : {}),
      l: LIST_TYPE.SERVER,
    };
    this.router.navigate(['/sajaseungeo'], {queryParams}).then();
  }

  public onClickDialect($event: MouseEvent): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.globalEventSvc.Click($event);

    // const [detail_id, list_id] = this.lastIdSvc.getLastDialectId();
    const queryParams: SearchParam = {
      // ...(detail_id ? {id: detail_id} : {}),
      // ...(list_id ? {start: list_id} : {}),
      l: LIST_TYPE.SERVER,
    };
    this.router.navigate(['/dialect'], {queryParams}).then();
  }

  public onClickProverb($event: MouseEvent): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.globalEventSvc.Click($event);

    // const [detail_id, list_id] = this.lastIdSvc.getLastProverbId();
    const queryParams: SearchParam = {
      // ...(detail_id ? {id: detail_id} : {}),
      // ...(list_id ? {start: list_id} : {}),
      l: LIST_TYPE.SERVER,
    };
    this.router.navigate(['/proverb'], {queryParams}).then();
  }

  public onClickExample($event: MouseEvent): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.globalEventSvc.Click($event);

    const queryParams: SearchParam = {
      l: LIST_TYPE.SERVER,
    };
    this.router.navigate(['/example'], {queryParams}).then();
  }

  public onClickHomonym($event: MouseEvent): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.globalEventSvc.Click($event);

    // const [detail_id, list_id] = this.lastIdSvc.getLastHomonymId();
    const queryParams: SearchParam = {
      // ...(detail_id ? {id: detail_id} : {}),
      // ...(list_id ? {start: list_id} : {}),
    };

    // const routeHomonym = this.optionSvc.homonymDisp === HOMONYM_DISP_TYPE.SIMPLE ? '/homonym2' : '/homonym';
    const routeHomonym = '/homonym';
    this.router.navigate([routeHomonym], {queryParams}).then();
  }

  /**
   * 인사말 눌렀을 때 처리 함수. 다음 인사말을 가져온다.
   */
  onClickGreeting(): void {
    this.greetingClick$.next();
  }

  private serverInfoRequested = false;

  private checkServerInfo(): void {
    // 중복 호출 방지
    if (this.serverInfoRequested) {
      return;
    }
    this.serverInfoRequested = true;

    // SystemService의 CheckAppVersion 메서드 사용
    this.systemSvc.CheckAppVersion(
      (title, message) => this.lm.OpenMsgPopupAlert(message, title)
    ).subscribe(
      () => {}, // 성공 시 아무 작업 안함
      console.error // 오류 발생 시 콘솔에 출력
    );
  }
}
